
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { useAuth } from "@/hooks/use-auth";
import { Copy, Loader2 } from "lucide-react";

interface DuplicateColumnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId: string;
  columnName: string;
  boardId: string;
}

export function DuplicateColumnDialog({
  open,
  onOpenChange,
  columnId,
  columnName,
  boardId,
}: DuplicateColumnDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedBoardId, setSelectedBoardId] = useState<string>("");
  const [newColumnName, setNewColumnName] = useState(`${columnName} (Cópia)`);

  // Fetch clients using the same logic as useRealtimeClients
  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ["clients-for-duplication"],
    queryFn: async () => {
      if (!user) return [];
      
      console.log('Loading clients for user:', user.id);

      // First get the user's company_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        return [];
      }

      let targetCompanyId = userData?.company_id;

      // If user doesn't have company_id, check if they're a collaborator
      if (!targetCompanyId) {
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id, permissions')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          targetCompanyId = collaboratorData.company_id;
          console.log('User is collaborator, using company:', targetCompanyId);
        }
      }

      if (!targetCompanyId) {
        console.log('No company_id found for user');
        return [];
      }

      console.log('Loading clients for company:', targetCompanyId);

      // Load clients only for the user's company
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .eq('company_id', targetCompanyId)
        .order('name');
      
      if (error) {
        console.error('Error loading clients:', error);
        return [];
      }

      console.log('Loaded clients:', data);
      return data || [];
    },
    enabled: !!user && open,
  });

  // Fetch boards for selected client - Fixed query to avoid duplicates
  const { data: boards = [], isLoading: isLoadingBoards } = useQuery({
    queryKey: ["boards-for-client", selectedClientId],
    queryFn: async () => {
      if (!selectedClientId) return [];
      
      const { data, error } = await supabase
        .from("kanban_boards")
        .select("id, name")
        .eq("client_id", selectedClientId)
        .eq("is_archived", false)
        .order("name");
      
      if (error) {
        console.error('Error loading boards:', error);
        return [];
      }
      
      // Remove duplicates based on id (just in case)
      const uniqueBoards = data?.filter((board, index, self) => 
        index === self.findIndex(b => b.id === board.id)
      ) || [];
      
      console.log('Loaded boards for client:', selectedClientId, uniqueBoards);
      return uniqueBoards;
    },
    enabled: !!selectedClientId && open,
  });

  // Duplication mutation
  const duplicateColumnMutation = useMutation({
    mutationFn: async () => {
      if (!selectedBoardId || !newColumnName.trim()) {
        throw new Error("Selecione um quadro e defina um nome para a coluna");
      }

      // Get the original column data
      const { data: originalColumn, error: columnError } = await supabase
        .from("kanban_columns")
        .select("*")
        .eq("id", columnId)
        .single();

      if (columnError) throw columnError;

      // Get the cards from the original column
      const { data: originalCards, error: cardsError } = await supabase
        .from("kanban_cards")
        .select("*")
        .eq("column_id", columnId)
        .eq("is_archived", false)
        .order("position");

      if (cardsError) throw cardsError;

      // Get the next position for the new column
      const { data: existingColumns, error: positionError } = await supabase
        .from("kanban_columns")
        .select("position")
        .eq("board_id", selectedBoardId)
        .order("position", { ascending: false })
        .limit(1);

      if (positionError) throw positionError;

      const nextPosition = existingColumns.length > 0 ? existingColumns[0].position + 1 : 1;

      // Create the new column
      const { data: newColumn, error: newColumnError } = await supabase
        .from("kanban_columns")
        .insert({
          board_id: selectedBoardId,
          name: newColumnName.trim(),
          position: nextPosition,
          is_goal_column: false, // Reset goal column status for duplicated columns
        })
        .select()
        .single();

      if (newColumnError) throw newColumnError;

      // Duplicate all cards
      if (originalCards && originalCards.length > 0) {
        const cardsToInsert = originalCards.map((card, index) => ({
          column_id: newColumn.id,
          title: card.title,
          description: card.description,
          position: index + 1,
          assignee: card.assignee,
          assignee_avatar: card.assignee_avatar,
          due_date: card.due_date,
          is_completed: false, // Reset completion status for duplicated cards
          is_archived: false,
        }));

        const { error: insertCardsError } = await supabase
          .from("kanban_cards")
          .insert(cardsToInsert);

        if (insertCardsError) throw insertCardsError;
      }

      return newColumn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-columns"] });
      queryClient.invalidateQueries({ queryKey: ["kanban-cards"] });
      toast.success("Coluna duplicada com sucesso!");
      onOpenChange(false);
      setSelectedClientId("");
      setSelectedBoardId("");
      setNewColumnName(`${columnName} (Cópia)`);
    },
    onError: (error: any) => {
      console.error("Error duplicating column:", error);
      toast.error(error.message || "Erro ao duplicar coluna");
    },
  });

  const handleDuplicate = () => {
    duplicateColumnMutation.mutate();
  };

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedBoardId(""); // Reset board selection when client changes
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Duplicar Coluna
          </DialogTitle>
          <DialogDescription>
            Duplicar a coluna "{columnName}" com todos os seus cartões para outro quadro.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="client-select">Cliente de Destino</Label>
            <Select value={selectedClientId} onValueChange={handleClientChange}>
              <SelectTrigger>
                <SelectValue placeholder={isLoadingClients ? "Carregando clientes..." : "Selecione um cliente"} />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clients.length === 0 && !isLoadingClients && (
              <p className="text-xs text-muted-foreground">Nenhum cliente encontrado</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="board-select">Quadro de Destino</Label>
            <Select 
              value={selectedBoardId} 
              onValueChange={setSelectedBoardId}
              disabled={!selectedClientId}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  !selectedClientId 
                    ? "Selecione um cliente primeiro" 
                    : isLoadingBoards 
                    ? "Carregando quadros..." 
                    : "Selecione um quadro"
                } />
              </SelectTrigger>
              <SelectContent>
                {boards.map((board) => (
                  <SelectItem key={board.id} value={board.id}>
                    {board.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedClientId && boards.length === 0 && !isLoadingBoards && (
              <p className="text-xs text-muted-foreground">Nenhum quadro encontrado para este cliente</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="column-name">Nome da Nova Coluna</Label>
            <Input
              id="column-name"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              placeholder="Nome da coluna duplicada"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDuplicate}
            disabled={!selectedBoardId || !newColumnName.trim() || duplicateColumnMutation.isPending}
          >
            {duplicateColumnMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Duplicar Coluna
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
